import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import type { CreateOrganizationSuggestionPayload } from '@/api/v4/organization-suggestions.api';
import { createOrganizationSuggestions } from '@/api/v4/organization-suggestions.api';
import { QueryKey } from '@/config/query-client';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';

import { useExploreForm } from './form/use-explore-form';
import { isRegeneratePanelOpenState } from './regenerate/regenerate-explore.state';
import { exploreFormState } from './explore.state';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

export const useExploreSuggestions = () => {
  const queryClient = useQueryClient();
  const navigateTo = useNavigateTo();
  const { requestId: routeRequestId } = useParams();
  const { description, exampleCompanies, locations, isValid } =
    useExploreForm();

  const isRegeneratePanelOpen = useAtomValue(isRegeneratePanelOpenState);

  const exploreFormStateData = useAtomValue(exploreFormState);

  const payload: CreateOrganizationSuggestionPayload = {
    problemDescription: description,
    ...(locations.length && {
      locations: locations.map(option => JSON.parse(option.value)),
    }),
    ...(exampleCompanies.length && {
      companies: exampleCompanies.map(company => ({
        crunchbaseId: company.id,
        name: company.name,
        website: company.website,
        logoUrl: company.logoUrl,
      })),
    }),
    model: exploreFormStateData.model,
    enableExploreAccountData: exploreFormStateData.enabledExploreAccountData,
  };

  const generateSuggestions = async () =>
    await createOrganizationSuggestions(payload);

  const query = useQuery({
    queryKey: [QueryKey.ExploreSuggestions],
    queryFn: generateSuggestions,
    staleTime: 1000 * 60 * 3,
    enabled: isValid && !isRegeneratePanelOpen && !routeRequestId,
    retry: false,
  });

  const requestId = query.data?.requestId;
  const navigatedRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!requestId || navigatedRequestIdRef.current === requestId) return;

    navigatedRequestIdRef.current = requestId;
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ExploreHistory] });
    navigateTo.exploreResults({ requestId });
  }, [requestId, queryClient, navigateTo]);

  return query;
};
